import { Client } from '@stomp/stompjs'
import SockJS from 'sockjs-client'
import Cookies from 'js-cookie'
import { useAuthStore } from '@/store/AuthStore'
import { runRefreshOnce } from '@/utils/api'

const isTokenExpiredOrExpiringSoon = () => {
    const token = Cookies.get('access_token')
    if (!token) return true
    try {
        const payload = JSON.parse(atob(token.split('.')[1]))
        return payload.exp * 1000 < Date.now() + 60_000
    } catch {
        return true
    }
}

const getWsBaseUrl = () => {
    const base = (window.env && window.env.BASE_URL)
        ? window.env.BASE_URL
        : 'http://localhost:8080/api/v1'
    return base.replace('/api/v1', '')
}

class ChatSocketService {
    constructor() {
        this._client = null
        this._isConnected = false
        // destination → callback, сохраняем все активные подписки для реконнекта
        this._registry = new Map()
        // destination → живой STOMP sub объект
        this._liveSubs = new Map()
    }

    connect() {
        if (this._client && this._client.active) return

        this._client = new Client({
            webSocketFactory: () => new SockJS(`${getWsBaseUrl()}/ws`),
            reconnectDelay: 5000,
            beforeConnect: async () => {
                try {
                    if (isTokenExpiredOrExpiringSoon()) {
                        const authStore = useAuthStore()
                        const newToken = await runRefreshOnce(authStore)
                        if (!newToken) {
                            // Refresh token невалиден — останавливаем реконнекты
                            this._client.deactivate()
                            return
                        }
                    }
                } catch (e) {
                    console.warn('Token refresh before WS connect failed:', e)
                    this._client.deactivate()
                    return
                }
                const token = Cookies.get('access_token')
                this._client.connectHeaders = {
                    Authorization: token ? `Bearer ${token}` : '',
                }
            },
            onConnect: () => {
                this._isConnected = true
                this._liveSubs.clear()
                // Восстанавливаем все подписки после (ре)коннекта
                this._registry.forEach((callback, destination) => {
                    this._doSubscribe(destination, callback)
                })
            },
            onDisconnect: () => {
                this._isConnected = false
                this._liveSubs.clear()
            },
            onStompError: (frame) => {
                console.error('STOMP error:', frame)
            },
        })

        this._client.activate()
    }

    disconnect() {
        if (this._client) {
            this._client.deactivate()
            this._client = null
        }
        this._isConnected = false
        this._registry.clear()
        this._liveSubs.clear()
    }

    subscribeToChatTopic(chatId, callback) {
        return this._subscribe(`/topic/chat/${chatId}`, callback)
    }

    subscribeToCompanyFeed(companyId, callback) {
        return this._subscribe(`/topic/company/${companyId}`, callback)
    }

    subscribeToNotifications(callback) {
        return this._subscribe('/user/queue/notifications', callback)
    }

    unsubscribe(subscription) {
        if (subscription) {
            subscription.unsubscribe()
        }
    }

    _subscribe(destination, callback) {
        this._registry.set(destination, callback)

        if (this._isConnected) {
            this._doSubscribe(destination, callback)
        }
        // Если не подключены — подпишемся в onConnect через registry

        return {
            unsubscribe: () => {
                this._registry.delete(destination)
                const live = this._liveSubs.get(destination)
                if (live) {
                    live.unsubscribe()
                    this._liveSubs.delete(destination)
                }
            }
        }
    }

    _doSubscribe(destination, callback) {
        const sub = this._client.subscribe(destination, (frame) => {
            try {
                callback(JSON.parse(frame.body))
            } catch (e) {
                console.error('Ошибка обработки STOMP сообщения:', e)
            }
        })
        this._liveSubs.set(destination, sub)
    }
}

export const chatSocket = new ChatSocketService()
